import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import { Chip, Tooltip, alpha, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialColor } from "hooks/useMaterials";
import { useFormatNumber } from "src/utils/formatNumber";
import { MaterialBalance } from "./types";
import { useLayerWeightFormat } from "./useLayerWeightFormat";

interface Props {
  balances: MaterialBalance[];
  massScaleFactor: number;
}

export const MaterialBalanceBar = ({ balances, massScaleFactor }: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const formatNumber = useFormatNumber();
  const { unitLabel, decimalPlaces } = useLayerWeightFormat(massScaleFactor);
  const { getColor } = useMaterialColor();
  const formatScaled = formatNumber(decimalPlaces, true);

  const imbalanced = balances.filter((balance) => !balance.isWithinTolerance);
  if (imbalanced.length === 0) {
    return null;
  }

  return (
    <>
      {imbalanced.map((balance) => {
        const materialColor = getColor(balance.materialName);
        const scaledBaseline = balance.baseline * massScaleFactor;
        const scaledCurrent = balance.current * massScaleFactor;
        const scaledDelta = balance.delta * massScaleFactor;
        const status: "added" | "over" | "under" =
          balance.baseline === 0 ? "added" : scaledDelta > 0 ? "over" : "under";
        const tooltip =
          status === "added"
            ? t("materialBalanceAdded", {
                material: balance.materialName,
                value: `${formatScaled(scaledCurrent)}${unitLabel}`,
              })
            : status === "over"
              ? t("materialBalanceOver", {
                  material: balance.materialName,
                  current: `${formatScaled(scaledCurrent)}${unitLabel}`,
                  target: `${formatScaled(scaledBaseline)}${unitLabel}`,
                  value: `${formatScaled(Math.abs(scaledDelta))}${unitLabel}`,
                })
              : t("materialBalanceUnder", {
                  material: balance.materialName,
                  current: `${formatScaled(scaledCurrent)}${unitLabel}`,
                  target: `${formatScaled(scaledBaseline)}${unitLabel}`,
                  value: `${formatScaled(Math.abs(scaledDelta))}${unitLabel}`,
                });
        const trailing =
          status === "added"
            ? `${formatScaled(scaledCurrent)}${unitLabel} (${t("materialBalanceNewSuffix")})`
            : `${formatScaled(scaledCurrent)}${unitLabel} (${scaledDelta > 0 ? "+" : ""}${formatScaled(scaledDelta)}${unitLabel})`;
        return (
          <Tooltip key={balance.materialName} title={tooltip} arrow>
            <Chip
              size="small"
              variant="outlined"
              icon={<PriorityHighRoundedIcon fontSize="small" />}
              label={`${balance.materialName} ${trailing}`}
              sx={{
                bgcolor: alpha(materialColor, 0.15),
                borderColor: alpha(materialColor, 0.5),
                fontWeight: 600,
                "& .MuiChip-icon": {
                  color: theme.palette.error.main,
                },
              }}
              data-testid={`material-balance-${balance.materialName}-${status}`}
            />
          </Tooltip>
        );
      })}
    </>
  );
};
